// Generieke beoordeling (FO-048 P1) — gedeelde DTOs + Zod.
//
// Generaliseert de productniveau-`ArtikelBeoordeling` + exemplaarniveau-
// `UitrustingItemBeoordeling` naar één target-agnostisch model. Een medewerker
// geeft een score 1-5 (+ optionele opmerking) aan een artikel, een uitgereikt
// uitrustingstuk of een opleiding. Prompts (`BeoordelingsVerzoek`) worden door
// de cron klaargezet (na cursus-afronding + jaarlijks); invullen sluit het
// verzoek. Eén bron van waarheid: dit schema valideert in de API én het portal.

import { z } from 'zod'
// Hergebruik de score+comment-regels van de bestaande artikel-beoordeling.
import { CreateBeoordelingSchema, type ArtikelBeoordelingDTO } from './artikel-beoordeling'

export const BEOORDELING_TARGET_TYPES = ['ARTIKEL', 'UITRUSTING_ARTIKEL', 'OPLEIDING'] as const
export type BeoordelingTargetType = (typeof BEOORDELING_TARGET_TYPES)[number]

export const BEOORDELING_TARGET_LABELS: Record<BeoordelingTargetType, string> = {
  ARTIKEL: 'Artikel',
  UITRUSTING_ARTIKEL: 'Uitrusting',
  OPLEIDING: 'Opleiding',
}

export type BeoordelingVerzoekBron = 'CURSUS_AFRONDING' | 'PERIODIEK'
export type BeoordelingVerzoekStatus = 'OPEN' | 'INGEVULD' | 'VERLOPEN'

export const BEOORDELING_VERZOEK_BRON_LABELS: Record<BeoordelingVerzoekBron, string> = {
  CURSUS_AFRONDING: 'Na afronden cursus',
  PERIODIEK: 'Periodieke check',
}

/**
 * Portal-submit: een score voor één target. Hergebruikt `CreateBeoordelingSchema`
 * (score 1-5 + opmerking) en voegt het generieke target toe. Hetzelfde schema
 * valideert client-side in het portal-form en server-side in de API.
 */
export const SubmitBeoordelingSchema = CreateBeoordelingSchema.extend({
  targetType: z.enum(BEOORDELING_TARGET_TYPES),
  targetId: z.string().min(1, 'Kies wat je beoordeelt'),
})
export type SubmitBeoordelingInput = z.infer<typeof SubmitBeoordelingSchema>

/** Eén open/ingevuld beoordelingsverzoek voor de portal "te beoordelen"-lijst. */
export interface BeoordelingsVerzoekDTO {
  id: string
  targetType: BeoordelingTargetType
  targetId: string
  bron: BeoordelingVerzoekBron
  status: BeoordelingVerzoekStatus
  deadline: string | null
  /** Leesbare omschrijving van wat beoordeeld wordt (opleidingsnaam / artikel / item). */
  targetLabel: string
  /** Optionele subregel (categorie, maat, datum cursus). */
  targetSubLabel: string | null
  /** Reeds gegeven score van deze medewerker (null = nog niet ingevuld). */
  eigenScore: number | null
  createdAt: string
}

/** Aggregatie per target (backoffice-overzicht: gemiddelde + verdeling). */
export interface BeoordelingTargetAggregatieDTO {
  targetType: BeoordelingTargetType
  targetId: string
  targetLabel: string
  gemiddelde: number | null
  aantal: number
  /** Lengte 5: index 0..4 = aantal scores 1..5. */
  verdeling: number[]
}

/**
 * Detail-inzage per target (backoffice, FO-090): de kop-aggregatie plus de losse
 * oordelen mét naam/score/opmerking/datum. Namen zijn hier zichtbaar voor
 * bevoegde backoffice-inzage; de portal blijft naamloos (anonimiteit naar
 * collega's). Hergebruikt `ArtikelBeoordelingDTO` voor de losse oordelen, zodat
 * de bestaande `ArtikelBeoordelingenSection` het paneel kan renderen.
 */
export interface BeoordelingDetailDTO extends BeoordelingTargetAggregatieDTO {
  beoordelingen: ArtikelBeoordelingDTO[]
}

/**
 * Querystring voor `GET /beoordelingen/detail` (FO-090). Eén bron van waarheid:
 * valideert server-side in de API én client-side voor de filterbalk. De filters
 * (score / periode / medewerker) worden server-side op de query toegepast, niet
 * alleen in de UI; zonder waarde = geen filter ("alles").
 */
export const BeoordelingDetailQuerySchema = z.object({
  targetType: z.enum(BEOORDELING_TARGET_TYPES),
  targetId: z.string().min(1, 'targetId is verplicht'),
  /** Filter op exacte score 1-5 (leeg = alle scores). */
  score: z.coerce.number().int().min(1).max(5).optional(),
  /** Periode-ondergrens op `createdAt` (yyyy-mm-dd, Europe/Amsterdam-dag). */
  van: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'van moet yyyy-mm-dd zijn')
    .optional(),
  /** Periode-bovengrens op `createdAt`, inclusief die dag (yyyy-mm-dd). */
  totEnMet: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'totEnMet moet yyyy-mm-dd zijn')
    .optional(),
  /** Filter op één medewerker (de gever van het oordeel). */
  employeeId: z.string().min(1).optional(),
})
export type BeoordelingDetailQuery = z.infer<typeof BeoordelingDetailQuerySchema>
